import { computed, DestroyRef, effect, inject, Injectable, linkedSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { CertificationStore } from '../../../croisement-référentiel/store/certification.store';
import { LivrableCaractériséStore } from '../../../livrables-caractérisés/store/livrable-caracterisé.store';
import { InterrelationStore } from '../../../référentiel-métier/référentiel-metier-interrelations/store/interrelation.store';
import { Aptitude } from '../../../référentiel-métier/référentiel-métier-processus/processus-aptitude/models/aptitude.model';
import { Caractéristique } from '../../../référentiel-métier/référentiel-métier-périmètre/perimetre-caracteristiques/models/caractéristique.model';
import { Livrable } from '../../../référentiel-métier/référentiel-métier-périmètre/perimetre-metier/models/livrable.model';
import { SidepanelService } from '../../../shared/components/sidepanel/sidepanel.service';
import { FormControlNames, FormGroupNames } from '../../models/form-control-names.model';
import { EtapeStore } from '../../parcours-etape/store/etape.store';
import { ModuleStore } from '../../parcours-module/store/module.store';
import { Référentiel } from '../../../administration/gestion-référentiels/models/référentiel.model';

@Injectable({
  providedIn: 'root',
})
export class ModuleDetailFormService {
  readonly #fb = inject(FormBuilder);
  readonly #moduleStore = inject(ModuleStore);
  readonly #livrableCaractériséStore = inject(LivrableCaractériséStore);
  readonly #etapeStore = inject(EtapeStore);
  readonly #interrelationStore = inject(InterrelationStore);
  readonly #certificationStore = inject(CertificationStore);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);

  moduleSelectionne = computed(() => this.#moduleStore.selectedEntity());
  etapeSelectionnee = computed(() => this.#etapeStore.selectedEntity());

  readonly referentiel = linkedSignal<Référentiel | null>(() => null);

  readonly certificationAttachee = linkedSignal(() => this.#certificationStore.selectedEntity());
  readonly aptitudesCertif = linkedSignal(
    () =>
      this.certificationAttachee()
        ?.blocsCompetences.flatMap((x) => x.competences)
        .flatMap((x) => x.aptitudes ?? [])
        .filter((apt1, i, list) => list.findIndex((apt2) => apt2.id === apt1.id) === i)
        .map((aptitude) => ({
          ...aptitude,
          interrelations: this.#interrelationStore.parPhase(aptitude.idPhase),
        })) ?? [],
  );

  readonly aptitudesFromEtape = linkedSignal<Aptitude[]>(() => {
    const aptitudesAjoutéesManuellement = this.etapeSelectionnee()?.aptitudes ?? [];
    return [...aptitudesAjoutéesManuellement, ...this.aptitudesCertif()];
  });

  aptitudesDisponibles = linkedSignal<Aptitude[]>(() => {
    const module = this.moduleSelectionne();
    let aptitudes = this.aptitudesFromEtape() ?? [];
    if (module && module.aptitudes.length > 0) {
      aptitudes = aptitudes.filter((x) => !module.aptitudes.some((y) => x.id === y.id));
    }
    return aptitudes;
  });

  form: FormGroup = this.#fb.group({
    [FormGroupNames.GENERAL]: this.#fb.group({
      [FormControlNames.NOM]: [null],
      [FormControlNames.DUREE]: [null, [Validators.min(1)]],
      [FormControlNames.CODE]: [{ value: null, disabled: true }],
    }),
    [FormGroupNames.OBJECTIFS]: this.#fb.array([]),
    [FormGroupNames.LIVRABLES]: this.#fb.group({
      [FormControlNames.SOUS_CARACTERISTIQUES]: [[]],
      [FormControlNames.LIVRABLES]: [[]],
    }),
  });

  constructor() {
    effect(() => {
      const module = this.moduleSelectionne();
      if (module) {
        this.#etapeStore.selection(module.idEtape);
        this.objectifs.clear({ emitEvent: false });
        this.form.patchValue(
          {
            [FormGroupNames.GENERAL]: {
              [FormControlNames.NOM]: module.nom,
              [FormControlNames.DUREE]: module.duree ?? null,
              [FormControlNames.CODE]: module.codeModule,
            },
            [FormGroupNames.OBJECTIFS]: [],
            [FormGroupNames.LIVRABLES]: {
              [FormControlNames.SOUS_CARACTERISTIQUES]: module.caracteristiques,
              [FormControlNames.LIVRABLES]: module.livrablesCaracterises.map((x) => x.livrable),
            },
          },
          { emitEvent: false },
        );

        module.aptitudes.forEach((x) => this.addAptitude(x));
      } else {
        this.form.reset();
      }
    });

    effect(() => {
      const etape = this.etapeSelectionnee();
      if (etape) {
        const certification = etape.certification;
        const referentiel = this.referentiel();
        if (certification && referentiel) {
          this.#certificationStore.parCodeFiche({ codeFiche: certification.codeFiche, idReferentiel: referentiel.id });
        } else {
          this.#certificationStore.unselect();
        }
      }
    });

    this.objectifs.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.#destroyRef), debounceTime(1000))
      .subscribe(() => {
        let aptitudes = this.aptitudesFromEtape()
          ? this.aptitudesFromEtape().filter(
              (x) => !this.objectifs.value.flatMap((x) => x.aptitude).some((y) => x.id === y.id),
            )
          : [];
        this.aptitudesDisponibles.set(aptitudes);
      });
  }

  submitModule() {
    const module = this.moduleSelectionne();
    if (!module) return;

    const idModuleSelectionne = module.id;

    this.#sidepanelService.loading.set(true);

    this.#moduleStore.modifier({
      id: idModuleSelectionne,
      request: {
        indice: module.indice,
        nom: this.nom.value,
        duree: this.duree.value,
        idsAptitudes: this.objectifs.value?.flatMap((x) => x.aptitude.id),
        aptitudesInterrelations: [
          ...this.objectifs?.value
            .flatMap((x) => x.interrelations as { id: string }[])
            .map((x) => x.id.split('-', 2))
            .map(([idAptitude, idInterrelation]) => ({ idAptitude, idInterrelation })),
        ],
        idsProductions: [...new Set(this.objectifs?.value.flatMap((x) => x.productions).map((x) => x.id))],
        idsCaracteristiques: this.caracteristiques.value.map((x) => x.id),
        idsLivrables: this.livrables.value.map((x) => x.id),
        aptitudesEvaluees: this.objectifs.value
          .filter((x) => x.estEvaluee)
          .map((x) => ({ idAptitude: x.aptitude.id, objectifApprentissage: x.objectif })),
      },
      next: () => {
        this.#livrableCaractériséStore.charger(idModuleSelectionne);
        this.#sidepanelService.loading.set(false);
      },
    });
  }

  addAptitude(aptitude: Aptitude) {
    const module = this.moduleSelectionne();
    if (!module) return;

    const aptitudeEvaluée = module.aptitudesEvaluees?.find((x) => x.idAptitude === aptitude.id);

    const interrelationsDeLAptitude = module.interrelations
      .filter((interrelation) => interrelation.idAptitude === aptitude.id)
      .map((x) => ({ id: `${x.idAptitude}-${x.idInterrelation}` }));

    const productionsDeLAptitude = module.productions.filter((production) =>
      aptitude.productions?.some((p) => p.id === production.id),
    );

    const objectifForm = this.#fb.group({
      aptitude: [aptitude],
      interrelations: [interrelationsDeLAptitude],
      productions: [productionsDeLAptitude],
      estEvaluee: [aptitudeEvaluée !== undefined],
      objectif: [aptitudeEvaluée?.objectifApprentissage],
    });

    this.objectifs.push(objectifForm, { emitEvent: false });
  }

  get general(): FormGroup {
    return this.form.get(FormGroupNames.GENERAL) as FormGroup;
  }

  get objectifs(): FormArray<FormGroup> {
    return this.form.get(FormGroupNames.OBJECTIFS) as FormArray<FormGroup>;
  }

  get livrablesGroup(): FormGroup {
    return this.form.get(FormGroupNames.LIVRABLES) as FormGroup;
  }

  get nom() {
    return this.general.get(FormControlNames.NOM) as FormControl<string>;
  }

  get duree() {
    return this.general.get(FormControlNames.DUREE) as FormControl<number>;
  }

  get livrables() {
    return this.livrablesGroup.get(FormControlNames.LIVRABLES) as FormControl<Livrable[]>;
  }

  get caracteristiques() {
    return this.livrablesGroup.get(FormControlNames.SOUS_CARACTERISTIQUES) as FormControl<Caractéristique[]>;
  }
}
